import { useCallback, useEffect, useState } from "react";
import { TensileRequirementByGrade } from "../../../models/TensileRequirementByGrade";

export interface TensileColumnDef {
	field: keyof TensileRequirementByGrade;
	headerName: string;
	headerGroup?: string;
	editable?: boolean;
	minWidth?: number;
	maxWidth?: number;
	flex?: number;
	align?: "left" | "center" | "right";
	valueParser?: (value: string) => any;
	formatValue?: (value: any) => string;
}

interface EditingCell {
	rowIndex: number;
	field: keyof TensileRequirementByGrade;
	value: string;
}

interface SelectedCell {
	rowIndex: number;
	colIndex: number;
	field: keyof TensileRequirementByGrade;
}

interface UseTensileTableCellEditingProps {
	rows: TensileRequirementByGrade[];
	columns: TensileColumnDef[];
	onCellUpdate: (
		rowIndex: number,
		field: keyof TensileRequirementByGrade,
		value: any,
	) => void;
	getEditValue?: (
		row: TensileRequirementByGrade,
		field: keyof TensileRequirementByGrade,
	) => string;
}

export const useTensileTableCellEditing = ({
	rows,
	columns,
	onCellUpdate,
	getEditValue: getEditValueProp,
}: UseTensileTableCellEditingProps) => {
	const getEditValue = useCallback(
		(
			row: TensileRequirementByGrade,
			field: keyof TensileRequirementByGrade,
		): string => {
			if (getEditValueProp) {
				return getEditValueProp(row, field);
			}
			const value = row[field];
			return value == null ? "" : String(value);
		},
		[getEditValueProp],
	);
	const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
	const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);

	const getEditableColumns = useCallback(() => {
		return columns
			.map((col, index) => ({ col, index }))
			.filter(({ col }) => col.editable === true);
	}, [columns]);

	const isCellEditable = useCallback((col: TensileColumnDef) => {
		return col.editable === true;
	}, []);

	const findNextEditableCell = useCallback(
		(
			currentRowIndex: number,
			currentColIndex: number,
			direction: "up" | "down" | "left" | "right",
		): { rowIndex: number; colIndex: number } | null => {
			const editableColumns = getEditableColumns();
			if (editableColumns.length === 0) return null;

			let currentEditableColIdx = editableColumns.findIndex(
				(ec) => ec.index === currentColIndex,
			);

			if (currentEditableColIdx === -1) {
				currentEditableColIdx = 0;
			}

			if (direction === "left") {
				if (currentEditableColIdx > 0) {
					return {
						rowIndex: currentRowIndex,
						colIndex: editableColumns[currentEditableColIdx - 1].index,
					};
				}
				return null;
			}

			if (direction === "right") {
				if (currentEditableColIdx < editableColumns.length - 1) {
					return {
						rowIndex: currentRowIndex,
						colIndex: editableColumns[currentEditableColIdx + 1].index,
					};
				}
				return null;
			}

			// For up/down, navigate rows
			const step = direction === "up" ? -1 : 1;
			const nextRowIndex = currentRowIndex + step;

			if (nextRowIndex >= 0 && nextRowIndex < rows.length) {
				return { rowIndex: nextRowIndex, colIndex: currentColIndex };
			}

			return null;
		},
		[rows.length, getEditableColumns],
	);

	const handleCellClick = useCallback(
		(
			row: TensileRequirementByGrade,
			col: TensileColumnDef,
			rowIndex: number,
			colIndex: number,
		) => {
			if (!isCellEditable(col)) return;

			if (
				selectedCell?.rowIndex === rowIndex &&
				selectedCell?.colIndex === colIndex
			) {
				return;
			}

			setSelectedCell({
				rowIndex,
				colIndex,
				field: col.field,
			});
			setEditingCell(null);
		},
		[selectedCell, isCellEditable],
	);

	const handleCellDoubleClick = useCallback(
		(
			row: TensileRequirementByGrade,
			col: TensileColumnDef,
			rowIndex: number,
			colIndex: number,
		) => {
			if (!isCellEditable(col)) return;

			const displayValue = getEditValue(row, col.field);

			setSelectedCell({
				rowIndex,
				colIndex,
				field: col.field,
			});
			setEditingCell({
				rowIndex,
				field: col.field,
				value: displayValue,
			});
		},
		[isCellEditable, getEditValue],
	);

	const enterEditMode = useCallback(() => {
		if (!selectedCell) return;

		const row = rows[selectedCell.rowIndex];
		const col = columns[selectedCell.colIndex];

		if (!row || !col || !isCellEditable(col)) return;

		const displayValue = getEditValue(row, col.field);

		setEditingCell({
			rowIndex: selectedCell.rowIndex,
			field: col.field,
			value: displayValue,
		});
	}, [selectedCell, rows, columns, isCellEditable, getEditValue]);

	const handleCellChange = useCallback((value: string) => {
		setEditingCell((prev) => (prev ? { ...prev, value } : null));
	}, []);

	const handleCellBlur = useCallback(() => {
		if (!editingCell) return;

		const col = columns.find((c) => c.field === editingCell.field);
		if (!col) {
			setEditingCell(null);
			return;
		}

		const parsedValue = col.valueParser
			? col.valueParser(editingCell.value)
			: editingCell.value;

		onCellUpdate(editingCell.rowIndex, editingCell.field, parsedValue);

		setEditingCell(null);
	}, [editingCell, columns, onCellUpdate]);

	const handleEditKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			e.stopPropagation();

			if (e.key === "Enter") {
				e.preventDefault();
				handleCellBlur();
			} else if (e.key === "Escape") {
				e.preventDefault();
				setEditingCell(null);
			} else if (e.key === "Tab") {
				e.preventDefault();
				handleCellBlur();
				if (selectedCell) {
					const next = findNextEditableCell(
						selectedCell.rowIndex,
						selectedCell.colIndex,
						e.shiftKey ? "left" : "right",
					);
					if (next) {
						const col = columns[next.colIndex];
						if (col) {
							setSelectedCell({
								rowIndex: next.rowIndex,
								colIndex: next.colIndex,
								field: col.field,
							});
						}
					}
				}
			} else if (
				["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
			) {
				e.preventDefault();
				handleCellBlur();
				if (selectedCell) {
					const direction = e.key.replace("Arrow", "").toLowerCase() as
						| "up"
						| "down"
						| "left"
						| "right";
					const next = findNextEditableCell(
						selectedCell.rowIndex,
						selectedCell.colIndex,
						direction,
					);
					if (next) {
						const col = columns[next.colIndex];
						if (col) {
							setSelectedCell({
								rowIndex: next.rowIndex,
								colIndex: next.colIndex,
								field: col.field,
							});
						}
					}
				}
			}
		},
		[handleCellBlur, selectedCell, findNextEditableCell, columns],
	);

	// Handle click outside to clear selection
	useEffect(() => {
		if (!selectedCell) return;

		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			const isTableCell = target.closest("[data-cell-id]");
			if (!isTableCell) {
				setSelectedCell(null);
				setEditingCell(null);
			}
		};

		document.addEventListener("click", handleClickOutside, true);
		document.addEventListener("contextmenu", handleClickOutside, true);

		return () => {
			document.removeEventListener("click", handleClickOutside, true);
			document.removeEventListener("contextmenu", handleClickOutside, true);
		};
	}, [selectedCell]);

	// Handle keyboard events for navigation
	useEffect(() => {
		if (!selectedCell || editingCell) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			const { key } = e;

			if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)) {
				e.preventDefault();
				const direction = key.replace("Arrow", "").toLowerCase() as
					| "up"
					| "down"
					| "left"
					| "right";
				const next = findNextEditableCell(
					selectedCell.rowIndex,
					selectedCell.colIndex,
					direction,
				);
				if (next) {
					const col = columns[next.colIndex];
					if (col) {
						setSelectedCell({
							rowIndex: next.rowIndex,
							colIndex: next.colIndex,
							field: col.field,
						});
					}
				}
			}

			if (key === "Enter" || key === "F2") {
				e.preventDefault();
				enterEditMode();
			}

			if (key === "Tab") {
				e.preventDefault();
				const next = findNextEditableCell(
					selectedCell.rowIndex,
					selectedCell.colIndex,
					e.shiftKey ? "left" : "right",
				);
				if (next) {
					const col = columns[next.colIndex];
					if (col) {
						setSelectedCell({
							rowIndex: next.rowIndex,
							colIndex: next.colIndex,
							field: col.field,
						});
					}
				}
			}

			if (key === "Escape") {
				e.preventDefault();
				setSelectedCell(null);
			}

			// Start typing to enter edit mode
			if (key.length === 1 && /^[a-zA-Z0-9.-]$/.test(key)) {
				e.preventDefault();
				const col = columns[selectedCell.colIndex];
				if (col && isCellEditable(col)) {
					setEditingCell({
						rowIndex: selectedCell.rowIndex,
						field: col.field,
						value: key,
					});
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [
		selectedCell,
		editingCell,
		findNextEditableCell,
		enterEditMode,
		columns,
		isCellEditable,
	]);

	const clearSelection = useCallback(() => {
		setSelectedCell(null);
		setEditingCell(null);
	}, []);

	return {
		editingCell,
		selectedCell,
		handleCellClick,
		handleCellDoubleClick,
		handleCellChange,
		handleCellBlur,
		handleEditKeyDown,
		clearSelection,
	};
};
